/**
 * Session Thunks
 *
 * Handles session initialization with the backend and JWT token retrieval.
 *
 * Every session requires a JWT token from the backend:
 * - New session: call init API with empty body → get sessionId + token
 * - Existing session (reload/URL): call init API with sessionId → get token
 * - No session (blank chat): skip init, no API call needed
 *
 * The JWT token is stored in sessionStorage and automatically included
 * in all API calls via buildHeaders().
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { initSessionApi } from '@/api/sessionApi';
import {
  getSessionIdFromStorage,
  setSessionIdInStorage,
  setJwtToken,
  getJwtToken,
  clearSessionId,
  clearJwtToken,
  createSessionInUrl,
  getSessionIdFromUrl,
} from '@/shared/utils/session';
import { logger } from '@/shared/utils/logger';

/**
 * Call init API once and store sessionId + JWT token.
 * For new sessions, sessionId is omitted (backend generates it).
 * For existing sessions, sessionId is passed to get a fresh JWT.
 * No retry — failures are surfaced immediately so callers can handle them.
 */
async function attemptSessionInit(existingSessionId: string | undefined): Promise<string> {
  const response = await initSessionApi(existingSessionId);

  setSessionIdInStorage(response.sessionId);
  setJwtToken(response.token);

  if (!existingSessionId) {
    createSessionInUrl(response.sessionId);
  }

  logger.info('[Session] Initialized:', response.sessionId);
  return response.sessionId;
}

/**
 * Restore session on page load and fetch JWT token.
 *
 * - Storage/URL has session → call init API with sessionId to get JWT token
 * - Neither → no session yet (blank chat state), returns empty string
 *
 * URL session ID takes priority over storage — it represents the user's
 * explicit intent (e.g. shared link, bookmark). On failure the session ID
 * is preserved in storage so subsequent flows (createSessionThunk,
 * sendMessage) can retry with the same ID instead of creating a new one.
 */
export const initSessionThunk = createAsyncThunk<string, void, { rejectValue: string }>(
  'session/init',
  async (_, { rejectWithValue }) => {
    const storedId = getSessionIdFromStorage();
    const urlId = getSessionIdFromUrl();

    // Storage has session but URL cleared → user wants new chat
    if (storedId && !urlId) {
      logger.info('[Session] URL cleared, removing old session');
      clearSessionId();
      return '';
    }

    // URL takes priority — it's the explicit navigation intent
    const existingId = urlId ?? storedId;
    if (existingId) {
      try {
        // Keep storage in sync with the URL session ID
        if (urlId && storedId !== urlId) {
          setSessionIdInStorage(urlId);
        }

        const response = await initSessionApi(existingId);
        setJwtToken(response.token);

        logger.info('[Session] Restored with JWT:', existingId);
        return existingId;
      } catch (error) {
        logger.error('[Session] Failed to get JWT for existing session:', error);
        // Preserve the session ID — only clear the JWT token.
        // This lets createSessionThunk retry with the same session ID
        // instead of silently creating a new one.
        clearJwtToken();
        return rejectWithValue(error instanceof Error ? error.message : 'Session restore failed');
      }
    }

    // Neither → blank chat state (no API call needed)
    return '';
  },
);

/**
 * Ensure a session with a valid JWT token exists.
 *
 * Three scenarios:
 * 1. sessionId + JWT both exist → return immediately (fast path)
 * 2. sessionId exists but JWT missing → call init with sessionId to get JWT
 * 3. Neither exists → call init with empty body to create new session + JWT
 *
 * Also checks the URL for a session ID so that a URL-provided session is
 * never silently replaced with a brand-new one.
 *
 * Called before the first message and before any API call that needs auth.
 */
export const createSessionThunk = createAsyncThunk<string, void, { rejectValue: string }>(
  'session/create',
  async (_, { rejectWithValue }) => {
    const existingId = getSessionIdFromStorage() ?? getSessionIdFromUrl();
    const existingToken = getJwtToken();

    // Both exist — nothing to do
    if (existingId && existingToken) return existingId;

    try {
      // sessionId exists but JWT missing — fetch JWT for existing session
      if (existingId) {
        return await attemptSessionInit(existingId);
      }

      // No session at all — create new session + JWT
      return await attemptSessionInit(undefined);
    } catch (error) {
      logger.error('[Session] Create failed:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Session creation failed');
    }
  },
);
